import {
  formatAED,
  formatDueDate,
  formatPlanPrice,
  type BillingDiscountView,
  type MyBusiness,
  type OwnerBilling,
} from "@teamna/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Button,
  ErrorState,
  Field,
  FormRow,
  FormSection,
  InlineError,
  Notice,
  SkeletonList,
  SuccessNotice,
  WriteLock,
  showToast,
} from "../components/index.ts";
import { queryKeys } from "@teamna/web-client/hooks/keys.ts";
import { useBillingSession, useOwnerBilling, useSetBillingEmail } from "@teamna/web-client/hooks/use-owner-billing.ts";
import { AssistantPackageSection } from "./assistant/package.tsx";
import { OfficePage } from "./office.tsx";

/**
 * Billing (subscription billing spec, section 6.1): the business's own Teamna plan, for the owner
 * and an admin. What the plan costs, the discount, the credit, the card, the next charge, and the
 * email that gets the invoices. "Add card" and "Manage billing" open Stripe's own pages, so card
 * data never touches Teamna; Stripe sends the owner back here.
 *
 * Always open, also when Desk is read-only: this is the page that ends the read-only time.
 */

const DAY_MS = 86_400_000;

/** "4 days left", "1 day left", "ends today". */
function daysLeft(end: string, now: Date): string {
  const days = Math.ceil((Date.parse(end) - now.getTime()) / DAY_MS);
  if (days <= 0) return "Ends today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

/** The status line at the top of the page. */
export function billingStatusLine(billing: OwnerBilling, now: Date, timeZone: string): string {
  const date = (value: string | null) => (value ? formatDueDate(new Date(value), timeZone) : "");
  if (billing.access === "read_only") {
    if (billing.reason === "ended") return "Ended. Write to hello@teamna.app to start the plan again.";
    if (billing.reason === "paused_read_only") return `Paused${billing.pausedUntil ? ` until ${date(billing.pausedUntil)}` : ""}. Desk is read-only.`;
    return "Read-only. Add a card to make changes again.";
  }
  switch (billing.state) {
    case "trialing":
      if (billing.card && billing.nextCharge) return `Free trial. Your first charge is on ${date(billing.nextCharge.at)}.`;
      return billing.trialEndsAt ? `Free trial. ${daysLeft(billing.trialEndsAt, now)}.` : "Free trial.";
    case "active":
      return billing.nextCharge
        ? `Active. Next charge ${formatAED(billing.nextCharge.amountFils)} on ${date(billing.nextCharge.at)}.`
        : "Active.";
    case "canceling":
      return `Cancelled. Your plan ends on ${date(billing.currentPeriodEnd)}.`;
    case "past_due":
      return billing.graceEndsAt ? `Payment failed. Add a card before ${date(billing.graceEndsAt)}.` : "Payment failed. Add a card.";
    case "paused":
      return `Paused${billing.pausedUntil ? ` until ${date(billing.pausedUntil)}` : ""}. No charges until then.`;
    case "ended":
      return "Ended.";
    case null:
      return "Free trial.";
  }
}

/** "AED 75 off until 1 February 2027", "20% off, no end date", "100% off your next invoice". */
export function discountLine(discount: BillingDiscountView, timeZone: string): string {
  const off = discount.percentOff !== null ? `${discount.percentOff}% off` : `${formatAED(discount.amountOffFils ?? 0)} off`;
  if (discount.duration === "forever") return `${off}, no end date`;
  if (discount.duration === "once") return `${off} your next invoice`;
  if (discount.endsAt) return `${off} until ${formatDueDate(new Date(discount.endsAt), timeZone)}`;
  return `${off} for ${discount.months ?? 1} month${discount.months === 1 ? "" : "s"}`;
}

function openInBrowser(url: string): void {
  window.location.assign(url);
}

export function OwnerBillingScreen({
  cardAdded = false,
  now,
  openUrl = openInBrowser,
  back,
}: {
  /** Stripe sent the owner back from "Add card" (`?card=added`). */
  cardAdded?: boolean;
  now?: Date;
  /** Opens a Stripe page. Tests replace it. */
  openUrl?: (url: string) => void;
  /** The link above the title: Settings, where the page opens from in Desk. */
  back?: { to: string; label: string };
}) {
  return (
    <WriteLock reason={null}>
      <OfficePage {...(back ? { back } : {})} title="Billing" description="Your Teamna plan, your card and your invoices.">
        {(business) => <BillingBody business={business} cardAdded={cardAdded} now={now ?? new Date()} openUrl={openUrl} />}
      </OfficePage>
    </WriteLock>
  );
}

function BillingBody({
  business,
  cardAdded,
  now,
  openUrl,
}: {
  business: MyBusiness;
  cardAdded: boolean;
  now: Date;
  openUrl: (url: string) => void;
}) {
  const queryClient = useQueryClient();
  const billing = useOwnerBilling(business.businessId);
  const session = useBillingSession(business.businessId);
  const [thanks, setThanks] = useState(cardAdded);

  // Back from Stripe with a new card: the webhook usually lands within seconds. Read the plan again
  // until the card shows (at most about half a minute), and the business list for the banner.
  const waitingForCard = thanks && billing.data !== undefined && billing.data.card === null;
  useEffect(() => {
    if (!waitingForCard) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      void billing.refetch();
      if (tries >= 10) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [waitingForCard, billing]);
  useEffect(() => {
    if (cardAdded) void queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
  }, [cardAdded, queryClient]);

  if (billing.isLoading && !billing.data) return <SkeletonList rows={3} />;
  if (billing.error && !billing.data) return <ErrorState error={billing.error} onRetry={() => void billing.refetch()} />;
  const data = billing.data!;

  if (data.pilotSince) {
    return (
      <div>
        <Notice tone="info" title="Teamna pilot">
          Your business is in the Teamna pilot. There is no charge. Write to hello@teamna.app if you have a question about your plan.
        </Notice>
      </div>
    );
  }

  if (!data.enabled) {
    return (
      <div>
        <Notice tone="info" title="Online billing is not on yet">
          Teamna does not charge this business online yet. Write to hello@teamna.app if you have a question about your plan.
        </Notice>
      </div>
    );
  }

  const open = (kind: "card" | "portal") =>
    session.mutate(kind, {
      onSuccess: (result) => openUrl(result.url),
    });

  const timeZone = business.timezone;
  return (
    <div className="flex flex-col gap-6">
      {thanks ? (
        <SuccessNotice
          title="Card saved"
          message={data.card ? "Thank you. We saved your card." : "Thank you. Your card shows here in a moment."}
          onDismiss={() => setThanks(false)}
        />
      ) : null}
      {session.error ? <InlineError error={session.error} /> : null}

      <FormSection title="Plan" description={billingStatusLine(data, now, timeZone)}>
        <FormRow label="Price">
          <p className="text-[14px]">Teamna, {formatPlanPrice({ priceAed: data.priceFils / 100, interval: "month" })}</p>
        </FormRow>
        {data.discount ? (
          <FormRow label="Discount">
            <p className="text-[14px]">{discountLine(data.discount, timeZone)}</p>
          </FormRow>
        ) : null}
        {data.creditFils > 0 ? (
          <FormRow label="Credit">
            <p className="text-[14px]">{formatAED(data.creditFils)} credit. We use it on your next invoices.</p>
          </FormRow>
        ) : null}
        {data.nextCharge ? (
          <FormRow label="Next charge">
            <p className="text-[14px]">
              {formatAED(data.nextCharge.amountFils)} on {formatDueDate(new Date(data.nextCharge.at), timeZone)}
            </p>
          </FormRow>
        ) : null}
      </FormSection>

      <FormSection
        title="Card"
        description="Stripe keeps your card. Teamna never sees the card number."
      >
        <FormRow label={data.card ? "Card on file" : "No card"} hint={cardHint(data)}>
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            {data.card ? (
              <>
                <p className="text-[14px]">
                  {brandName(data.card.brand)} ending {data.card.last4}, expires {String(data.card.expMonth).padStart(2, "0")}/{data.card.expYear}
                </p>
                <Button loading={session.isPending && session.variables === "portal"} onClick={() => open("portal")}>
                  Manage billing
                </Button>
              </>
            ) : data.state === "ended" ? (
              <Button variant="ghost" loading={session.isPending && session.variables === "portal"} onClick={() => open("portal")}>
                See invoices
              </Button>
            ) : (
              <>
                <Button
                  disabled={!data.billingEmail}
                  loading={session.isPending && session.variables === "card"}
                  onClick={() => open("card")}
                >
                  Add card
                </Button>
                <Button variant="ghost" loading={session.isPending && session.variables === "portal"} onClick={() => open("portal")}>
                  See invoices or cancel
                </Button>
              </>
            )}
          </div>
        </FormRow>
      </FormSection>

      <AssistantPackageSection business={business} />

      {/* Keyed on the saved address, so a save from elsewhere starts the field again from it. */}
      <BillingEmail key={data.billingEmail ?? ""} businessId={business.businessId} current={data.billingEmail} />
    </div>
  );
}

function cardHint(data: OwnerBilling): string {
  if (data.state === "ended") return "Your plan ended. Write to hello@teamna.app to start it again.";
  if (data.card) return "Change the card, see your invoices, or cancel your plan.";
  if (!data.billingEmail) return "Add a billing email first. We send the invoices to it.";
  if (data.needsCard) return "Add a card to keep full access. We charge it each month on the bill date.";
  return "Your plan costs nothing now, so a card is not needed yet. We ask for one 7 days before the first charge.";
}

function brandName(brand: string): string {
  const names: Record<string, string> = { visa: "Visa", mastercard: "Mastercard", amex: "American Express" };
  return names[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function BillingEmail({ businessId, current }: { businessId: string; current: string | null }) {
  const save = useSetBillingEmail(businessId);
  const [email, setEmail] = useState(current ?? "");
  const changed = email.trim().toLowerCase() !== (current ?? "");

  return (
    <FormSection title="Billing email" description="Stripe sends the invoices and the receipts to this address.">
      <div className="py-4">
        {save.error ? <InlineError error={save.error} /> : null}
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(email.trim(), { onSuccess: () => showToast("Billing email saved.") });
          }}
        >
          <div className="min-w-0 flex-1">
            <Field
              label="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              {...(current ? {} : { hint: "Add it before you add a card." })}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={!changed || email.trim() === ""} loading={save.isPending}>
            Save
          </Button>
        </form>
      </div>
    </FormSection>
  );
}
